/* import __COLOCATED_TEMPLATE__ from './filterable-list-router.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type ColumnData } from 'embercom/lib/ai-content-library/constants';
import {
  type FolderFilterOption,
  type SyncIssuesFilterOptions,
} from 'embercom/lib/content-service/search-api';
import { type TagOperator } from 'embercom/lib/knowledge-hub/constants';
import type KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import type Folder from 'embercom/models/content-service/folder';
import { type EntityType } from 'embercom/models/data/entity-types';
import { type SortDirection } from 'embercom/services/inbox-api';
import { type Predicate } from 'embercom/lib/knowledge-hub/constants';
import { SourceType, SourceTypeName } from 'embercom/models/import-service/import-source';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    title: string;
    folder?: Folder;
    setSearchTerm: (searchTerm?: string) => void;
    searchTerm?: string;
    setLinkedUrls: (linkedUrls?: string[]) => void;
    linkedUrls?: string[];
    titleTerm?: string;
    setTitleTerm: (titleTerm?: string) => void;
    bodyTerm?: string;
    setBodyTerm: (bodyTerm?: string) => void;
    urlTerm?: string;
    setUrlTerm: (urlTerm?: string) => void;
    listApi: KnowledgeHubApi;
    locale: string | null;
    setLocale: (locale: string | null) => void;
    predicates?: Array<Predicate>;
    setPredicates: (predicates: Array<Predicate>) => void;
    status: string | null;
    setStatus: (status: string | null) => void;
    syncStatus: string[] | null;
    setSyncStatus: (syncStatus: Array<string> | null) => void;
    chatbotState?: string;
    setChatbotState: (chatbotState?: string) => void;
    copilotState?: string;
    setCopilotState: (copilotState?: string) => void;
    createdBy?: string;
    setCreatedBy: (createdBy?: string) => void;
    selectedTypes?: Array<EntityType>;
    setSelectedTypes: (selectedTypes?: Array<EntityType>) => void;
    urlSourceId?: string;
    setUrlSourceId: (urlSourceId?: string) => void;
    selectedHelpCenterIds?: Array<string>;
    setSelectedHelpCenterIds: (selectedHelpCenterIds?: Array<string>) => void;
    lastUpdatedBy?: string;
    setLastUpdatedBy: (lastUpdatedBy?: string) => void;
    sortDirection?: SortDirection;
    sortBy?: string;
    setSortBy: (sortBy?: string) => void;
    setSortDirection: (sortDirection?: string) => void;
    displayFolderFilter?: boolean;
    selectedFolderFilterOption?: FolderFilterOption;
    setFolderFilterOption?: (folderFilterOption?: FolderFilterOption) => void;
    selectedSyncIssueOption?: SyncIssuesFilterOptions;
    setSyncIssueFilterOption?: (syncIssueOption?: SyncIssuesFilterOptions) => void;
    selectableColumns?: ColumnData[];
    resetFilters: () => void;
    activeFilter?: string;
    setActiveFilter: (filter?: string) => void;
    filtersApplied?: string[];
    setFiltersApplied: (filters: string[]) => void;
    tagIds?: string[];
    setTagIds: (tagIds?: string[]) => void;
    tagOperator: TagOperator;
    setTagOperator: (tagOperator: TagOperator) => void;
    collectionIds?: string[];
    setCollectionIds: (collectionIds?: string[]) => void;
    writtenByIds?: string[];
    setWrittenByIds: (writtenByIds?: string[]) => void;
    aiContentSegmentIds?: string[];
    setAiContentSegmentIds: (aiContentSegmentIds?: string[]) => void;
  };
}

/**
 * Wrapper component that conditionally renders the new Filterable List V2 interface
 * when the feature flag is enabled, otherwise falls back to the existing content view.
 */
export default class FilterableListRouter extends Component<Signature> {
  @service declare appService: AppService;

  /**
   * Determines if the new Filterable List V2 interface should be shown
   * based on the feature flag
   */
  get shouldShowNewInterface(): boolean {
    return (
      this.appService.app.canUseFeature('team-knowledge-foundations-synced-content-v2') &&
      this.args.folder?.sourceTypeName === SourceTypeName[SourceType.Web]
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentSetupV2::FilterableListRouter': typeof FilterableListRouter;
    'knowledge-hub/content-setup-v2//filterable-list-router': typeof FilterableListRouter;
  }
}
